import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";

const HERO_IMG = "/assets/hero-scoops.png";

const lines = [
  { text: "We turn", italic: false },
  { text: "artists into", italic: false },
  { text: "icons.", italic: true },
];

export const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });
  const imgX = useTransform(sx, (v) => v * 42);
  const imgY = useTransform(sy, (v) => v * 30);
  const dotX = useTransform(sx, (v) => v * -70);
  const dotY = useTransform(sy, (v) => v * -50);

  const onMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-[72px]"
    >
      <motion.div
        style={{ x: dotX, y: dotY }}
        className="absolute top-[18%] right-[8%] w-40 h-40 md:w-64 md:h-64 rounded-full bg-[#B5D5C5] -z-0"
      />
      <motion.div
        style={{ x: imgX, y: imgY }}
        className="absolute bottom-[6%] left-[4%] w-24 h-24 md:w-36 md:h-36 rounded-full bg-[#FF8E9E]/60"
      />

      <motion.div
        style={{ x: imgX, y: imgY }}
        className="absolute right-[6%] md:right-[14%] top-1/2 -translate-y-1/2 z-0 hidden sm:block"
      >
        <div className="arch-clip overflow-hidden w-[220px] h-[300px] md:w-[320px] md:h-[440px] shadow-[0_40px_80px_-20px_rgba(45,26,19,0.35)] animate-float-slow">
          <img
            src={HERO_IMG}
            alt="Stacked ice cream scoops illustration"
            data-testid="hero-image"
            className="w-full h-full object-cover"
          />
        </div>
        <motion.div
          initial={{ scale: 0, rotate: -30 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 1.4, type: "spring", stiffness: 120 }}
          className="absolute -top-5 -left-8 w-16 h-16 rounded-full bg-[#DDA77B] flex items-center justify-center text-[#2D1A13]"
        >
          <Sparkles size={24} strokeWidth={1.6} />
        </motion.div>
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-[11px] md:text-xs font-semibold uppercase tracking-[0.35em] text-[#2D1A13]/60 mb-8"
        >
          Ice Cream Social — Brand development studio for artists
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-serif-display font-black tracking-tighter leading-[0.92] text-[16vw] sm:text-[13vw] lg:text-[9.5rem] text-[#2D1A13]"
        >
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
              <motion.span
                className={`block ${line.italic ? "italic text-[#FF8E9E]" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 md:mt-16 flex flex-col md:flex-row md:items-end gap-10 md:gap-24">
          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-md text-base md:text-lg font-light leading-relaxed text-[#2D1A13]/75"
          >
            Identity, art direction and rollouts for musicians and visual artists.
            Premium strategy with a playful streak — served with a cherry on top.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-6"
          >
            <button
              data-testid="hero-cta-button"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full bg-[#2D1A13] text-[#FDFBF7] text-xs font-semibold uppercase tracking-[0.18em] px-9 py-5 hover:bg-[#FF8E9E] hover:text-[#2D1A13] transition-colors duration-300"
            >
              Start a Project
            </button>
            <button
              data-testid="hero-roster-link"
              onClick={() => document.getElementById("roster")?.scrollIntoView({ behavior: "smooth" })}
              className="group flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#2D1A13]/70 hover:text-[#2D1A13] transition-colors"
            >
              See the roster
              <ArrowDown size={15} className="group-hover:translate-y-1 transition-transform duration-300" />
            </button>
          </motion.div>
        </div>

        <motion.div
          data-testid="hero-stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-16 md:mt-24 flex gap-10 md:gap-16 text-[11px] uppercase tracking-[0.25em] text-[#2D1A13]/50 font-semibold"
        >
          <span>Artists first, always</span>
          <span>Boutique by design</span>
          <span>1 cherry on top</span>
        </motion.div>
      </div>
    </section>
  );
};
